"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { User, LogOut } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { routes } from "@/lib/routes";
import { strings } from "@/lib/strings";
import { AdminDropdown, AdminMobileMenu } from "@/components/layout/AdminDropdown";
import { AccountMenu, AccountMobileMenu } from "@/components/layout/AccountMenu";

/**
 * Desktop auth section: login + register, account menu, or admin controls.
 *
 * Login and registration are one flow (AuthFlow), but new visitors look for
 * "Registrieren" and do not recognise "Anmelden" as their way in. Registrieren
 * goes to the sign-up page, which pairs the form with the buy/sell/check paths.
 */
export function HeaderAuthDesktop() {
  const $ = _c(24);
  const {
    isAuthenticated,
    isAnonymous,
    isAdmin,
    openLoginModal,
    signOut,
    openProfileModal
  } = useAuth();
  const showAuthenticatedUI = isAuthenticated && !isAnonymous;
  if (!showAuthenticatedUI) {
    let t0;
    if ($[0] !== openLoginModal) {
      t0 = <button onClick={() => openLoginModal()} className="text-[15px] font-semibold text-primary hover:text-secondary transition-colors duration-200">{strings.auth.login}</button>;
      $[0] = openLoginModal;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Link href={routes.registrieren} className="px-[22px] py-[11px] text-[15px] font-bold rounded-full bg-neon text-primary hover:bg-neon/80 transition-all duration-200">{strings.auth.register}</Link>;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] !== t0) {
      t2 = <div className="flex items-center gap-5">{t0}{t1}</div>;
      $[3] = t0;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  if (isAdmin) {
    let t0;
    let t1;
    let t2;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <div className="w-px h-6 bg-surface-dim/50 mx-1" />;
      t1 = <AdminDropdown />;
      t2 = <div className="w-px h-6 bg-surface-dim/50 mx-1" />;
      $[5] = t0;
      $[6] = t1;
      $[7] = t2;
    } else {
      t0 = $[5];
      t1 = $[6];
      t2 = $[7];
    }
    let t3;
    if ($[8] !== openProfileModal) {
      t3 = () => openProfileModal();
      $[8] = openProfileModal;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    let t4;
    let t5;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <User className="w-4 h-4" aria-hidden={true} />;
      t5 = <span className="sr-only 2xl:not-sr-only">Mein Profil</span>;
      $[10] = t4;
      $[11] = t5;
    } else {
      t4 = $[10];
      t5 = $[11];
    }
    let t6;
    if ($[12] !== t3) {
      t6 = <button onClick={t3} title="Mein Profil" className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors text-foreground-muted hover:text-foreground hover:bg-surface-container-low">{t4}{t5}</button>;
      $[12] = t3;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let t7;
    if ($[14] !== signOut) {
      t7 = () => signOut();
      $[14] = signOut;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    let t8;
    let t9;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <LogOut className="w-4 h-4" aria-hidden={true} />;
      t9 = <span className="sr-only 2xl:not-sr-only">Abmelden</span>;
      $[16] = t8;
      $[17] = t9;
    } else {
      t8 = $[16];
      t9 = $[17];
    }
    let t10;
    if ($[18] !== t7) {
      t10 = <button onClick={t7} title="Abmelden" className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors text-foreground-muted hover:text-foreground hover:bg-surface-container-low">{t8}{t9}</button>;
      $[18] = t7;
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    let t11;
    if ($[20] !== t10 || $[21] !== t6) {
      t11 = <div className="hidden xl:flex items-center gap-2">{t0}{t1}{t2}{t6}{t10}</div>;
      $[20] = t10;
      $[21] = t6;
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    return t11;
  }
  let t0;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="hidden xl:flex items-center gap-2"><div className="w-px h-6 bg-surface-dim/50 mx-1" /><AccountMenu /></div>;
    $[23] = t0;
  } else {
    t0 = $[23];
  }
  return t0;
}
interface HeaderAuthMobileProps {
  onClose: () => void;
}

/**
 * Mobile auth section: register + login buttons, or account/admin menus.
 */
export function HeaderAuthMobile(t0) {
  const $ = _c(16);
  const {
    onClose
  } = t0;
  const {
    isAuthenticated,
    isAnonymous,
    isAdmin,
    openLoginModal
  } = useAuth();
  const showAuthenticatedUI = isAuthenticated && !isAnonymous;
  if (showAuthenticatedUI) {
    let t1;
    if ($[0] !== isAdmin || $[1] !== onClose) {
      t1 = isAdmin && <AdminMobileMenu onItemClick={onClose} />;
      $[0] = isAdmin;
      $[1] = onClose;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] !== onClose) {
      t2 = <AccountMobileMenu onItemClick={onClose} />;
      $[3] = onClose;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== t1 || $[6] !== t2) {
      t3 = <>{t1}{t2}</>;
      $[5] = t1;
      $[6] = t2;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    return t3;
  }
  let t1;
  if ($[8] !== onClose) {
    t1 = <Link href={routes.registrieren} onClick={onClose} className="block w-full text-center bg-neon text-primary py-3.5 rounded-full font-bold text-base hover:bg-neon/80 transition-colors">{strings.auth.register}</Link>;
    $[8] = onClose;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== onClose || $[11] !== openLoginModal) {
    t2 = <button onClick={() => {
      openLoginModal();
      onClose();
    }} className="w-full py-[13px] rounded-full font-bold text-base text-surface border border-surface/30 hover:bg-surface/10 transition-colors">{strings.auth.login}</button>;
    $[10] = onClose;
    $[11] = openLoginModal;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== t1 || $[14] !== t2) {
    t3 = <div className="flex flex-col gap-2.5">{t1}{t2}</div>;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
}